import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';
import { CARD_LABEL_SX } from 'src/components/actionCards';

const TOOLTIP_SX = { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 };

export default function ForecastCards({ forecasts, title }) {
    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `£${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `£${(value / 1000).toFixed(0)}K`;
        }
        return `£${value}`;
    };

    return (
        <Box sx={{ mb: 3 }}>
            {title && (
                <Tooltip title="Projected leads, consultations and revenue for upcoming months based on historical trends." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help', mb: 2 }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px',
                    '@media(max-width: 900px)': {
                        gridTemplateColumns: '1fr',
                    },
                }}
            >
                {forecasts.map((forecast, index) => (
                    <Box
                        key={index}
                        sx={{
                            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                            borderRadius: '16px',
                            p: '24px',
                            color: '#fff',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Background decoration */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -20,
                                right: -20,
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.05)',
                            }}
                        />

                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Iconify icon="eva:activity-fill" width={18} height={18} sx={{ opacity: 0.7 }} />
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    opacity: 0.8,
                                }}
                            >
                                {forecast.month}
                            </Typography>
                        </Box>

                        {/* Stats */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Tooltip title="Projected new leads for this month." placement="left" arrow
                                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'help' }}>
                                    <Typography sx={{ ...CARD_LABEL_SX, color: 'inherit', opacity: 0.7 }}>
                                        Leads
                                    </Typography>
                                    <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                                        {forecast.leads?.toLocaleString() || 0}
                                    </Typography>
                                </Box>
                            </Tooltip>

                            <Tooltip title="Projected consultations based on booking trends." placement="left" arrow
                                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'help' }}>
                                    <Typography sx={{ ...CARD_LABEL_SX, color: 'inherit', opacity: 0.7 }}>
                                        Consultations
                                    </Typography>
                                    <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                                        {forecast.consultations?.toLocaleString() || 0}
                                    </Typography>
                                </Box>
                            </Tooltip>

                            <Tooltip title="Estimated revenue based on current conversion rates and pricing." placement="left" arrow
                                slotProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        pt: 1.5,
                                        mt: 1,
                                        borderTop: '1px solid rgba(255,255,255,0.1)',
                                        cursor: 'help',
                                    }}
                                >
                                    <Typography sx={{ ...CARD_LABEL_SX, color: 'inherit', opacity: 0.7 }}>
                                        Projected Revenue
                                    </Typography>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#34d399' }}>
                                        {formatCurrency(forecast.revenue || 0)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                ))}
            </Box>

            {forecasts.length === 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        py: 6,
                        bgcolor: '#fff',
                        borderRadius: '16px',
                    }}
                >
                    <Typography sx={{ color: '#64748b' }}>
                        No forecast data available
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

ForecastCards.propTypes = {
    forecasts: PropTypes.arrayOf(
        PropTypes.shape({
            month: PropTypes.string.isRequired,
            leads: PropTypes.number,
            consultations: PropTypes.number,
            revenue: PropTypes.number,
        })
    ).isRequired,
    title: PropTypes.string,
};

ForecastCards.defaultProps = {
    title: '3-Month Forecast',
};
